import { Theme, ColorScheme, BorderRadius, Shadow, FontWeight } from '../types'
import { getFontFamilyValue, FONT_WEIGHT_VALUES } from '../data/fonts'

// Border radius to CSS value mapping
const BORDER_RADIUS_VALUES: Record<BorderRadius, string> = {
  none: '0',
  sm: '0.125rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  full: '9999px',
}

// Shadow to CSS value mapping
const SHADOW_VALUES: Record<Shadow, string> = {
  none: 'none',
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
}

// Generate CSS variables from a theme
export function themeToCSS(theme: Theme, isDarkMode: boolean): string {
  const colors = isDarkMode ? theme.darkMode : theme.lightMode

  return `
    --theme-primary: ${colors.primary};
    --theme-secondary: ${colors.secondary};
    --theme-accent: ${colors.accent};
    --theme-background: ${colors.background};
    --theme-surface: ${colors.surface};
    --theme-text: ${colors.text};
    --theme-text-muted: ${colors.textMuted};
    --theme-display-font: ${getFontFamilyValue(theme.displayFont)};
    --theme-body-font: ${getFontFamilyValue(theme.bodyFont)};
    --theme-display-weight: ${FONT_WEIGHT_VALUES[theme.displayWeight]};
    --theme-body-weight: ${FONT_WEIGHT_VALUES[theme.bodyWeight]};
    --theme-border-radius: ${BORDER_RADIUS_VALUES[theme.borderRadius]};
    --theme-shadow: ${SHADOW_VALUES[theme.shadow]};
  `.trim()
}

// Generate a style object for React components
export function themeToStyleObject(theme: Theme, isDarkMode: boolean): React.CSSProperties {
  const colors = isDarkMode ? theme.darkMode : theme.lightMode

  return {
    '--theme-primary': colors.primary,
    '--theme-secondary': colors.secondary,
    '--theme-accent': colors.accent,
    '--theme-background': colors.background,
    '--theme-surface': colors.surface,
    '--theme-text': colors.text,
    '--theme-text-muted': colors.textMuted,
    '--theme-display-font': getFontFamilyValue(theme.displayFont),
    '--theme-body-font': getFontFamilyValue(theme.bodyFont),
    '--theme-display-weight': FONT_WEIGHT_VALUES[theme.displayWeight],
    '--theme-body-weight': FONT_WEIGHT_VALUES[theme.bodyWeight],
    '--theme-border-radius': BORDER_RADIUS_VALUES[theme.borderRadius],
    '--theme-shadow': SHADOW_VALUES[theme.shadow],
  } as React.CSSProperties
}

// Generate a downloadable CSS file
export function generateCSSFile(theme: Theme): string {
  return `/* Theme: ${theme.name} */
/* Generated by Nimble Labs Theme Builder */

:root {
  /* Light Mode */
  --theme-primary: ${theme.lightMode.primary};
  --theme-secondary: ${theme.lightMode.secondary};
  --theme-accent: ${theme.lightMode.accent};
  --theme-background: ${theme.lightMode.background};
  --theme-surface: ${theme.lightMode.surface};
  --theme-text: ${theme.lightMode.text};
  --theme-text-muted: ${theme.lightMode.textMuted};

  /* Typography */
  --theme-display-font: ${getFontFamilyValue(theme.displayFont)};
  --theme-body-font: ${getFontFamilyValue(theme.bodyFont)};
  --theme-display-weight: ${FONT_WEIGHT_VALUES[theme.displayWeight]};
  --theme-body-weight: ${FONT_WEIGHT_VALUES[theme.bodyWeight]};

  /* Style */
  --theme-border-radius: ${BORDER_RADIUS_VALUES[theme.borderRadius]};
  --theme-shadow: ${SHADOW_VALUES[theme.shadow]};
}

@media (prefers-color-scheme: dark) {
  :root {
    /* Dark Mode */
    --theme-primary: ${theme.darkMode.primary};
    --theme-secondary: ${theme.darkMode.secondary};
    --theme-accent: ${theme.darkMode.accent};
    --theme-background: ${theme.darkMode.background};
    --theme-surface: ${theme.darkMode.surface};
    --theme-text: ${theme.darkMode.text};
    --theme-text-muted: ${theme.darkMode.textMuted};
  }
}

/* Dark mode class override */
.dark {
  --theme-primary: ${theme.darkMode.primary};
  --theme-secondary: ${theme.darkMode.secondary};
  --theme-accent: ${theme.darkMode.accent};
  --theme-background: ${theme.darkMode.background};
  --theme-surface: ${theme.darkMode.surface};
  --theme-text: ${theme.darkMode.text};
  --theme-text-muted: ${theme.darkMode.textMuted};
}

/* Google Fonts Import */
@import url('https://fonts.googleapis.com/css2?family=${encodeURIComponent(theme.displayFont).replace(/%20/g, '+')}:wght@${FONT_WEIGHT_VALUES[theme.displayWeight]}&family=${encodeURIComponent(theme.bodyFont).replace(/%20/g, '+')}:wght@${FONT_WEIGHT_VALUES[theme.bodyWeight]}&display=swap');
`
}
